import type { QuestionTask, ScoreboardUser, theme } from '@tryhackme/thm-ui-components';
import type { Chart, ChartOptions, LegendElement, LegendItem } from 'chart.js';
import { format } from 'date-fns';

export const handleLegendOnHover = (item: LegendItem, chart: Pick<Chart<'line'>, 'data' | 'update'>) => {
  for (const [index, dataSet] of chart.data.datasets.entries()) {
    if (item.text !== dataSet.label) {
      dataSet.backgroundColor = extendedLineColors[index]?.opaqueColor;
      dataSet.borderColor = extendedLineColors[index]?.opaqueColor;
    }
  }
  chart.update();
};

export const handleLegendOnLeave = (chart: Pick<Chart<'line'>, 'data' | 'update'>) => {
  for (const [index, dataSet] of chart.data.datasets.entries()) {
    dataSet.backgroundColor = extendedLineColors[index]?.backgroundColor;
    dataSet.borderColor = extendedLineColors[index]?.borderColor;
  }
  chart.update();
};

export const options: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 500,
  },
  elements: {
    point: {
      radius: 3,
      hoverRadius: 5,
      pointStyle: 'circle',
    },
  },

  plugins: {
    datalabels: {
      display: false,
    },
    legend: {
      position: 'bottom' as const,
      align: 'center',
      fullSize: true,
      labels: {
        pointStyle: 'circle',
        usePointStyle: true,
        boxWidth: 7,
        boxHeight: 7,
        padding: 20,
        font: { weight: '400' },
        textAlign: 'center',
      },
      onHover: (event, item, legend: LegendElement<'line'>) => handleLegendOnHover(item, legend.chart),
      onLeave: (event, item, legend) => handleLegendOnLeave(legend.chart),
    },
    title: {
      display: true,
      text: ' ',
    },

    tooltip: {
      caretPadding: 10,
      caretSize: 8,
      position: 'nearest',
      padding: 10,
      titleMarginBottom: 20,
      bodySpacing: 10,
      backgroundColor: 'white',
      titleColor: 'black',
      titleFont: { weight: 'bold', size: 12 },
      bodyColor: 'black',
      borderColor: '#e2e2e2',
      borderWidth: 1,
      usePointStyle: true,
      callbacks: {
        title: (context) => {
          const date = new Date(context[0]?.label ?? new Date());
          return `${format(date, 'MMM')} ${format(date, 'do')} at ${format(date, 'H')}:${format(date, 'mm')}`;
        },
        label: (context) => `  ${context.dataset.label ?? ''} : ${context.raw as string} points`,
        labelPointStyle: () => ({
          pointStyle: 'circle',
          rotation: 0,
        }),
      },
    },
  },
  interaction: {
    mode: 'index',
  },
  aspectRatio: 1,
  scales: {
    x: {
      display: false,
    },
    y: {
      type: 'linear',
      grid: { lineWidth: 2, drawTicks: false },
      ticks: { padding: 10 },
      suggestedMax: 5,
      beginAtZero: true,
      alignToPixels: true,
    },
  },
};
export const lightThemeOverride = {
  scales: {
    y: {
      ticks: { color: 'rgba(55, 61, 63, 1)' },
      grid: { color: 'rgba(240, 240, 240, 1)' },
    },
  },
  plugins: { legend: { labels: { color: 'rgba(55, 61, 63, 1)' } } },
} satisfies ChartOptions<'line'>;

export const darkThemeOverride = {
  scales: {
    y: {
      ticks: { color: 'rgba(238, 239, 242, 1)' },
      grid: { color: 'rgba(62, 71, 90, 1)' },
    },
  },
  plugins: { legend: { labels: { color: 'rgba(238, 239, 242, 1)' } } },
} satisfies ChartOptions<'line'>;

const lineColors = [
  {
    borderColor: 'rgb(0,143,251,1)',
    backgroundColor: 'rgb(0,143,251,1)',
    opaqueColor: 'rgb(0,143,251,0.1)',
  },
  {
    borderColor: 'rgba(0, 227, 150, 1)',
    backgroundColor: 'rgba(0, 227, 150, 1)',
    opaqueColor: 'rgba(0, 227, 150, 0.1)',
  },
  {
    borderColor: 'rgba(254, 176, 25, 1)',
    backgroundColor: 'rgba(254, 176, 25, 1)',
    opaqueColor: 'rgba(254, 176, 25, 0.1)',
  },
  {
    borderColor: 'rgba(255, 69, 96, 1)',
    backgroundColor: 'rgba(255, 69, 96, 1)',
    opaqueColor: 'rgba(255, 69, 96, 0.1)',
  },
  {
    borderColor: 'rgba(119, 93, 208, 1)',
    backgroundColor: 'rgba(119, 93, 208, 1)',
    opaqueColor: 'rgba(119, 93, 208, 0.1)',
  },
];

export const extendedLineColors = [...lineColors, ...lineColors, ...lineColors]; // 15 colors

export const getUserData = (labels: QuestionTask['timeCorrect'][], scoreboardUserData: ScoreboardUser) => {
  let totalScore = 0;
  const userProgressFlat = Object.values(scoreboardUserData.tasks)
    .flat<Omit<QuestionTask, 'submission'>[]>()
    .filter((progress) => !!progress.timeCorrect);

  const data = labels.map((label) => {
    const userProgress = userProgressFlat.find((progress) => progress.timeCorrect === label);
    if (userProgress) {
      totalScore += userProgress.score;
      return totalScore;
    }
    return totalScore;
  });

  return data;
};

export const getAspectRatio = (width: number, sizes: typeof theme.sizes) => {
  const lg = sizes.lg.split('px')[0] ?? 1024;
  const md = sizes.md.split('px')[0] ?? 768;
  const sm = sizes.sm.split('px')[0] ?? 360;

  if (width > +lg) return 6 / 2;
  if (width > +md) return 4 / 2;
  if (width > +sm) return 3 / 2;

  return 1;
};
